import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LuImage, LuShare2, LuUsers, LuZap, LuFolderOpen, LuSmartphone,
  LuShieldCheck, LuLock, LuKeyRound, LuCircleCheckBig, LuHardDrive,
  LuCheck, LuArrowRight, LuLayers, LuMenu, LuX
} from 'react-icons/lu';

const TIERS = [
  {
    id: 'free', name: 'Personal', price: 7, storage: '100 GB',
    features: ['Private photo storage', 'Secure sharing links', 'RAW file support', '25 albums', 'Email support'],
    cta: 'Start free trial'
  },
  {
    id: 'pro', name: 'Pro', price: 39, storage: '500 GB',
    features: ['Unlimited albums', 'Watermarks', 'Batch rename', 'Faster previews', 'Team seats available'],
    cta: 'Start free trial'
  },
  {
    id: 'studio', name: 'Studio', price: 99, storage: '2 TB', popular: true,
    features: ['Client galleries', 'Proofing & selection', 'Priority previews', 'Photo Vault archive', 'Team seats available'],
    cta: 'Start free trial'
  },
  {
    id: 'business', name: 'Business', price: 179, storage: '5 TB',
    features: ['Client uploads', 'Priority compute', 'Advanced analytics', '5 team seats', 'The Strip social feed'],
    cta: 'Start free trial'
  },
  {
    id: 'agency', name: 'Agency', price: 399, storage: '12 TB',
    features: ['White label', 'API access', 'SLA support', '20 team seats', 'Priority support (<24h)'],
    cta: 'Contact us'
  }
];

const FEATURES = [
  { icon: LuImage, title: 'Original-quality storage', desc: 'RAW, HEIC, JPEG, PNG: we preserve your originals with no forced compression. Download exactly what you uploaded.' },
  { icon: LuShare2, title: 'Private sharing', desc: 'Create links with expiration dates, passwords, watermarks, and download controls. Revoke access anytime.' },
  { icon: LuUsers, title: 'Client galleries', desc: 'Deliver proofs to clients with selection tools, download tracking, and branded presentations.' },
  { icon: LuZap, title: 'Fast processing', desc: 'Distributed burst processing handles RAW files across dedicated compute nodes. Variants generated in seconds.' },
  { icon: LuFolderOpen, title: 'Smart organization', desc: 'Albums, tags, favorites, and fast search. Build a library structure that matches how you think.' },
  { icon: LuSmartphone, title: 'Works everywhere', desc: 'Responsive web app that works on desktop, tablet, and mobile. Your library follows you.' }
];

const TRUST = [
  { icon: LuShieldCheck, title: 'No data sales', desc: 'Your photos are never sold, shared with advertisers, or monetized.' },
  { icon: LuLock, title: 'Private by default', desc: 'Everything starts private. Sharing is something you explicitly turn on.' },
  { icon: LuKeyRound, title: 'No AI training', desc: 'Your uploads are never used to train AI models, now or later.' },
  { icon: LuCircleCheckBig, title: 'You own your data', desc: 'Export your full library anytime. No lock-in, no hostage pricing.' }
];

const RAW_FORMATS = 'Canon CR2/CR3, Nikon NEF/NRW, Sony ARW, Adobe DNG, Fujifilm RAF, Olympus ORF, Panasonic RW2, Pentax PEF, HEIC, and more';

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Photo Harbor: Private Photo Management for Photographers';
  }, []);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="lp">
      {/* Nav */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-brand">
            <img src="/favicon/favicon-96x96.png" alt="" width={28} height={28} />
            <span>Photo Harbor</span>
          </Link>
          <div className="lp-nav-desktop">
            <Link to="/features">Features</Link>
            <a href="#pricing">Pricing</a>
            <a href="#trust">Privacy</a>
            <Link to="/login" className="lp-nav-signin">Sign in</Link>
            <Link to="/login?mode=signup" className="lp-nav-cta">Get started</Link>
          </div>
          <button className="lp-hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? <LuX size={22} /> : <LuMenu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="lp-mobile-menu">
          <Link to="/features" onClick={closeMenu}>Features</Link>
          <a href="#pricing" onClick={closeMenu}>Pricing</a>
          <a href="#trust" onClick={closeMenu}>Privacy</a>
          <Link to="/login" onClick={closeMenu}>Sign in</Link>
          <Link to="/login?mode=signup" className="lp-btn-primary" onClick={closeMenu}>
            Get started <LuArrowRight size={16} />
          </Link>
        </div>
      )}

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <h1 className="lp-h1">
            Your photos.<br />
            Your rules.<br />
            <span className="lp-h1-accent">No exceptions.</span>
          </h1>
          <p className="lp-hero-sub">
            Professional photo management built for photographers who value privacy and ownership.
            No ads. No AI training. No data sales.
          </p>
          <div className="lp-hero-actions">
            <Link to="/login?mode=signup" className="lp-btn-primary">
              Start your free trial <LuArrowRight size={16} />
            </Link>
            <a href="#pricing" className="lp-btn-secondary">See pricing</a>
          </div>
          <p className="lp-hero-note">14-day free trial on all plans. Cancel anytime.</p>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="lp-proof">
        <div className="lp-proof-inner">
          <div className="lp-proof-item">
            <strong>18+</strong>
            <span>RAW formats</span>
          </div>
          <div className="lp-proof-divider" />
          <div className="lp-proof-item">
            <strong>100%</strong>
            <span>Original quality</span>
          </div>
          <div className="lp-proof-divider" />
          <div className="lp-proof-item">
            <strong>0</strong>
            <span>AI training</span>
          </div>
          <div className="lp-proof-divider" />
          <div className="lp-proof-item">
            <strong>Private</strong>
            <span>by default</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="lp-features" id="features">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>Everything you need to manage and share your work</h2>
            <p>Built for working photographers, not social media.</p>
          </div>
          <div className="lp-features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lp-feature-card">
                <div className="lp-feature-icon"><f.icon size={20} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RAW support callout */}
      <section className="lp-raw">
        <div className="lp-section-inner">
          <div className="lp-raw-inner">
            <div className="lp-raw-icon"><LuLayers size={28} /></div>
            <div>
              <h3>Upload any format</h3>
              <p>
                {RAW_FORMATS}. Upload your camera originals and we generate optimized previews
                automatically using distributed compute.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Privacy */}
      <section className="lp-trust" id="trust">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>Privacy is not a feature. It's the foundation.</h2>
            <p>
              We're subscription-funded. Your photos are the product you're paying us to protect, not
              inventory for advertisers.
            </p>
          </div>
          <div className="lp-trust-grid">
            {TRUST.map((t) => (
              <div key={t.title} className="lp-trust-card">
                <div className="lp-trust-icon"><t.icon size={22} /></div>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
          <div className="lp-trust-cta">
            <Link to="/privacy" className="lp-btn-ghost">
              Read our privacy policy <LuArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-pricing" id="pricing">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>Simple, transparent pricing</h2>
            <p>Every plan includes a 14-day free trial. Upgrade, downgrade, or cancel anytime.</p>
          </div>
          <div className="lp-pricing-scroll">
            <div className="lp-pricing-grid">
              {TIERS.map((tier) => (
                <div key={tier.id} className={`lp-tier${tier.popular ? ' lp-tier-popular' : ''}`}>
                  {tier.popular && <div className="lp-tier-badge">Most popular</div>}
                  <h3>{tier.name}</h3>
                  <div className="lp-tier-price">
                    <span className="lp-tier-amount">${tier.price}</span>
                    <span className="lp-tier-period">/mo</span>
                  </div>
                  <div className="lp-tier-storage">
                    <LuHardDrive size={14} /> {tier.storage}
                  </div>
                  <ul className="lp-tier-features">
                    {tier.features.map((f) => (
                      <li key={f}><LuCheck size={14} /> {f}</li>
                    ))}
                  </ul>
                  <Link
                    to={tier.id === 'agency' ? '/login?mode=signup' : `/login?mode=signup&plan=${tier.id}`}
                    className={`lp-tier-cta${tier.popular ? ' primary' : ''}`}
                  >
                    {tier.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
          <div className="lp-pricing-note">
            <p>
              <strong>Extra storage:</strong> $29/TB/month on any plan.{' '}
              <strong>Photo Vault:</strong> $24/TB/month cold archive (Pro+).{' '}
              <strong>Team seats:</strong> $8/seat/month.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lp-final-cta">
        <div className="lp-section-inner">
          <h2>Store your photos somewhere that doesn't want to profit off them.</h2>
          <p>Start your 14-day free trial today. Cancel anytime.</p>
          <Link to="/login?mode=signup" className="lp-btn-primary lp-btn-lg">
            Start your free trial <LuArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img src="/favicon/favicon-96x96.png" alt="" width={24} height={24} />
            <span>Photo Harbor</span>
          </div>
          <div className="lp-footer-links">
            <Link to="/features">Features</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/security">Security</Link>
            <Link to="/guide">Guide</Link>
          </div>
          <div className="lp-footer-copy">
            &copy; {new Date().getFullYear()} Dailey Cloud. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
